import { useState, useEffect, useCallback, createContext, useContext, Component, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';

import SimpleOnboardingPage from './pages/SimpleOnboardingPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import HomePage from './pages/HomePage';
import SettingsPage from './pages/SettingsPage';

// Declare the global shopify object from App Bridge v4 CDN
declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
      config: { apiKey: string; shop: string; host: string; locale: string };
      toast: { show: (message: string, options?: { isError?: boolean; duration?: number }) => void };
    };
  }
}

// Helper to get URL parameters
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    shop: params.get('shop'),
    host: params.get('host') || '',
  };
}

// Authenticated fetch context
type AuthFetchFn = (url: string, options?: RequestInit) => Promise<Response>;
const AuthFetchContext = createContext<AuthFetchFn | null>(null);

export function useAuthenticatedFetch(): AuthFetchFn {
  const authFetch = useContext(AuthFetchContext);
  if (!authFetch) {
    // Fallback to regular fetch if context not available (development)
    return async (url: string, options?: RequestInit) => {
      return fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          'Content-Type': 'application/json',
        },
      });
    };
  }
  return authFetch;
}

// Error boundary to catch any initialization failures gracefully
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('Commerce Studio App Error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Commerce Studio</div>
          <p style={{ color: '#666', marginBottom: '16px' }}>
            Something went wrong loading the app.
          </p>
          <p style={{ color: '#999', fontSize: '14px', marginBottom: '24px' }}>
            {this.state.error?.message || 'Please try refreshing the page.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Inner app component that handles auth and routing
function AppContent({ shop }: { shop: string | null }) {
  // Connection status comes ONLY from our database (via onboarding-status ->
  // resolve-tenant). Nothing is cached in the browser: a browser flag was the
  // only thing keeping connected shops out of the login page, and it vanished
  // on a new browser, another staff member or Safari's storage expiry.
  // null = checking, 'error' = the check failed (retry, never the login page).
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | 'error' | null>(null);
  const [statusAttempt, setStatusAttempt] = useState(0);

  // Create authenticated fetch using App Bridge v4 CDN session tokens
  const authenticatedFetch = useCallback(async (url: string, options?: RequestInit): Promise<Response> => {
    try {
      // App Bridge v4: use window.shopify.idToken() for session tokens
      const token = window.shopify ? await window.shopify.idToken() : null;

      return fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
    } catch (error) {
      console.error('Failed to get session token:', error);
      // Fallback to regular fetch
      return fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          'Content-Type': 'application/json',
        },
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setOnboardingComplete(null);
    authenticatedFetch(`/api/commerce-studio/onboarding-status?shop=${shop}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data?.success === true && typeof data.onboardingComplete === 'boolean') {
          setOnboardingComplete(data.onboardingComplete);
        } else {
          setOnboardingComplete('error');
        }
      })
      .catch(() => {
        if (!cancelled) setOnboardingComplete('error');
      });
    return () => { cancelled = true; };
  }, [authenticatedFetch, shop, statusAttempt]);

  if (onboardingComplete === 'error') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }} data-testid="connection-status-error">
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>Commerce Studio</div>
        <p>We couldn't check this store's Commerce Studio connection. Please try again.</p>
        <button
          onClick={() => setStatusAttempt(n => n + 1)}
          style={{ marginTop: '16px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (onboardingComplete === null) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>Commerce Studio</div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <AuthFetchContext.Provider value={authenticatedFetch}>
      <BrowserRouter>
        {/* App Bridge v4 sidebar navigation — Home + Settings only.
            All per-widget configuration / activation / analytics lives
            in commerce.varai.ai (single source of truth — see
            feedback_plugin_single_source_of_truth). Home doubles as the
            simplified app install surface; Settings shows server
            connection status. */}
        <ui-nav-menu>
          <a href="/" rel="home">Home</a>
          <a href="/settings">Settings</a>
        </ui-nav-menu>
        <Routes>
          <Route
            path="/"
            element={onboardingComplete ? <HomePage /> : <SimpleOnboardingPage />}
          />
          <Route path="/onboarding" element={<SimpleOnboardingPage />} />
          <Route path="/plans" element={<PlanSelectionPage />} />
          <Route path="/billing" element={<PlanSelectionPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* /apps and /analytics were full app catalog + analytics dashboards
              with their own configuration UIs. Removed 2026-05-15 — both
              now redirect home so deep-links don't 404. The AppsPage and
              AnalyticsPage component files were deleted in the same commit. */}
          <Route path="/apps" element={<Navigate to="/home" replace />} />
          <Route path="/analytics" element={<Navigate to="/home" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthFetchContext.Provider>
  );
}

// Main App component
export default function App() {
  const { shop, host } = getUrlParams();

  // If not in Shopify admin context (no host parameter and no shopify global), show fallback
  if (!host && !window.shopify) {
    return (
      <AppProvider i18n={{}}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Commerce Studio</div>
          <p>Please access this app from your Shopify admin.</p>
          {shop && <p style={{ color: '#666' }}>Shop: {shop}</p>}
        </div>
      </AppProvider>
    );
  }

  return (
    <AppProvider i18n={{}}>
      <AppErrorBoundary>
        <AppContent shop={shop} />
      </AppErrorBoundary>
    </AppProvider>
  );
}
